import axios, {AxiosInstance, AxiosRequestConfig, AxiosResponse} from 'axios';
import {CookiesKey} from "@/app/utils/cookies";
import Cookies from "js-cookie";

export interface ErrorModel {
  error: string
  status: number
}

export const clearCookies = () => {
  Cookies.remove(CookiesKey.token);
  Cookies.remove(CookiesKey.user);
}

class ApiRequest {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: "https://back.padelmatchapp.com/api/v1",
      headers: {'Content-Type': 'application/json'},
    });

    this.client.interceptors.request.use(
      (config) => {
        const token = Cookies.get(CookiesKey.token);
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
        }
        return config;
      },
      (error) => {
        return Promise.reject(error);
      }
    );
  }

  async get<T>(url: string, {
    query,
    config,
  }: {
    query?: Record<string, any>;
    config?: AxiosRequestConfig;
  }): Promise<T> {
    try {
      const token = Cookies.get(CookiesKey.token);
      const headers = token ? {Authorization: `Bearer ${token}`} : {};

      const response: AxiosResponse<T> = await this.client.get(url, {
        ...config,
        params: query,
        headers: {
          ...config?.headers,
          ...headers,
        },
      });

      return response.data;
    } catch (error) {
      if (axios.isAxiosError(error)) {
        if (error.response) {
          if (error.response.status === 401) {
            clearCookies();
          }
          if (error.response.status === 403) {
            clearCookies();
            try {
              localStorage.clear();
            } catch {
              // ignore storage errors
            }
            window.location.href = '/signup';
          }
          if (error.response.status === 404) {
            clearCookies();
            try {
              localStorage.clear();
            } catch {
              // ignore storage errors
            }
            window.location.href = '/signup';
          }
          throw error.response.data;
        }
      }
      this.handleError(error);
      throw error;
    }
  }

  // POST method with token handling already implemented
  async post<T>(url: string, {
    data,
    query,
    config,
  }: {
    data?: any;
    query?: Record<string, any>;
    config?: AxiosRequestConfig;
  }): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.client.post(url, data, {
        ...config,
        params: query,
      });
      return response.data;
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        this.handleError(error);
        if (error.response) {
          if (error.response.status === 401) {
            clearCookies();
            window.location.href = '/';
          }
          throw error.response.data;
        }
      }
      throw error;
    }
  }

  // PUT method to update data on the server
  async put<T>(url: string, {
    data,
    query,
    config,
  }: {
    data?: any;
    query?: Record<string, any>;
    config?: AxiosRequestConfig;
  }): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.client.put(url, data, {
        ...config,
        params: query,
      });
      return response.data;
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        this.handleError(error);
        if (error.response) {
          if (error.response.status === 401) {
            clearCookies();
            window.location.href = '/';
          }
          throw error.response.data;
        }
      }
      throw error;
    }
  }

  async patch<T>(url: string, {
    data,
    query,
    config,
  }: {
    data?: any;
    query?: Record<string, any>;
    config?: AxiosRequestConfig;
  }): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.client.patch(url, data, {
        ...config,
        params: query,
      });
      return response.data;
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        this.handleError(error);
        if (error.response) {
          if (error.response.status === 401) {
            clearCookies();
            window.location.href = '/';
          }
          throw error.response.data;
        }
      }
      throw error;
    }
  }

  // DELETE method to remove data on the server
  async delete<T>(url: string, {
    query,
    config,
  }: {
    query?: Record<string, any>;
    config?: AxiosRequestConfig;
  }): Promise<T> {
    try {
      const response: AxiosResponse<T> = await this.client.delete(url, {
        ...config,
        params: query,
      });
      return response.data;
    } catch (error: any) {
      if (axios.isAxiosError(error)) {
        this.handleError(error);
        if (error.response) {
          if (error.response.status === 401) {
            clearCookies();
            window.location.href = '/';
          }
          throw error.response.data;
        }
      }
      throw error;
    }
  }

  // Handles and logs API errors
  private handleError(error: any): void {
    console.error('API Error:', error);
  }
}

const request = new ApiRequest();
export default request;
